import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './FilterPopover.module.css';
import { Icon } from './Icon';
export function FilterPopover({ activeCount, onClear, children, }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    // Recompute panel position whenever it opens, on scroll, on resize, on
    // window content change. We render the panel into document.body via portal
    // so it escapes any ancestor stacking context (page reveal animation on
    // .content > * creates one, which is what made the panel look "transparent"
    // before — Card was painted on top).
    useLayoutEffect(() => {
        if (!open)
            return;
        const update = () => {
            const t = triggerRef.current;
            if (!t)
                return;
            const r = t.getBoundingClientRect();
            setPos({
                top: r.bottom + 6,
                left: r.left,
                right: window.innerWidth - r.right,
            });
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        const close = (e) => {
            const target = e.target;
            if (triggerRef.current?.contains(target))
                return;
            if (panelRef.current?.contains(target))
                return;
            setOpen(false);
        };
        const key = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        window.addEventListener('mousedown', close);
        window.addEventListener('keydown', key);
        return () => {
            window.removeEventListener('mousedown', close);
            window.removeEventListener('keydown', key);
        };
    }, [open]);
    return (_jsxs("div", { className: s.root, children: [_jsxs("button", { ref: triggerRef, type: "button", className: s.trigger, "data-open": open ? 'true' : undefined, onClick: () => setOpen((v) => !v), children: [_jsx(Icon, { name: "filter", size: 13 }), "Filters", activeCount > 0 && _jsx("span", { className: s.badge, children: activeCount })] }), open && pos &&
                createPortal(_jsxs("div", { ref: panelRef, className: s.panel, role: "dialog", style: { top: pos.top, right: pos.right }, children: [_jsxs("div", { className: s.panelHead, children: [_jsx("span", { className: s.panelTitle, children: "Filters" }), _jsx("button", { type: "button", className: s.clearBtn, disabled: activeCount === 0, onClick: onClear, children: "Clear all" })] }), children] }), document.body)] }));
}
export function FilterRow({ label, children }) {
    return (_jsxs("div", { className: s.row, children: [_jsx("span", { className: s.rowLabel, children: label }), children] }));
}
export function CheckGroup({ options, value, onChange, }) {
    const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    return (_jsx("div", { className: s.checkGroup, children: options.map((opt) => {
            const checked = value.includes(opt.value);
            return (_jsxs("label", { className: s.checkItem, "data-active": checked || undefined, children: [_jsx("input", { type: "checkbox", checked: checked, onChange: () => toggle(opt.value) }), _jsx("span", { className: s.itemLabel, children: opt.label }), opt.count !== undefined && _jsx("span", { className: s.itemCount, children: opt.count })] }, opt.value));
        }) }));
}
export function RadioGroup({ options, value, onChange, }) {
    return (_jsx("div", { className: s.radioGroup, children: options.map((opt) => (_jsx("button", { type: "button", className: `${s.radioItem} ${opt.value === value ? s.radioActive : ''}`, onClick: () => onChange(opt.value), children: opt.label }, opt.value))) }));
}
